import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils.ts';
import { Button } from './button.tsx';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

type CalendarProps = Omit<React.ComponentProps<'div'>, 'onSelect'> & {
  selected?: Date | null;
  onSelect?: (date: Date) => void;
  /** Only single-date selection exists (kept for call-site compatibility). */
  mode?: 'single';
  showOutsideDays?: boolean;
  classNames?: Record<string, string>;
};

/** A dependency-free month grid with single-date selection. */
function Calendar({
  className,
  classNames: _classNames,
  showOutsideDays: _showOutsideDays = true,
  selected,
  onSelect,
  mode: _mode = 'single',
  ...props
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(() => (selected ? new Date(selected) : new Date()));

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const handlePrevMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const handleNextMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  const handleDateClick = (day: number) =>
    onSelect?.(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  const isSelected = (day: number) =>
    !!selected &&
    selected.toDateString() ===
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();

  const days: React.ReactNode[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="p-0 text-center text-sm" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(
      <Button
        key={day}
        type="button"
        onClick={() => handleDateClick(day)}
        variant={isSelected(day) ? 'default' : 'ghost'}
        aria-pressed={isSelected(day)}
        className={cn(
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
          isSelected(day) &&
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
        )}
      >
        {day}
      </Button>,
    );
  }

  return (
    <div data-slot="calendar" className={cn('p-3 bg-card-bg rounded-md', className)} {...props}>
      <div className="flex items-center justify-between mb-4">
        <Button
          type="button"
          variant="outline"
          aria-label="Previous month"
          className="h-7 w-7 p-0 bg-transparent"
          onClick={handlePrevMonth}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-medium">
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        <Button
          type="button"
          variant="outline"
          aria-label="Next month"
          className="h-7 w-7 p-0 bg-transparent"
          onClick={handleNextMonth}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {DAY_NAMES.map((name) => (
          <div
            key={name}
            className="text-center text-xs font-medium text-muted-foreground mb-1 h-9 flex items-center justify-center"
          >
            {name}
          </div>
        ))}
        {days}
      </div>
    </div>
  );
}

export { Calendar, type CalendarProps };
